import { computed, createVNode, defineComponent, ref, watch } from "vue";
import { CloseOutlined, DownOutlined, ExclamationCircleOutlined, LoadingOutlined, UpOutlined } from "@ant-design/icons-vue";
import { Button, Modal } from "ant-design-vue";
import { HelperState } from "@/store/room/interface";
import { useStore } from "vuex";
import { fmtMsg } from "vue-glcommonui";
import { CommonLocale, HelperLocales, LowBandWidthLocales, TeacherClassLocale } from "@/locales/localeid";
import { HelperService } from "@/services";
import { Logger } from "@/utils/logger";
import {
  VideoCameraIcon,
  VideoCameraSlashIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  XMarkIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowsPointingInIcon,
  WifiIcon,
} from "@heroicons/vue/20/solid";
import { generateAvatar } from "@/utils/utils";
import NameTag from "@/components/common/name-tag/name-tag.vue";
import { isUserPublishedAudio, isUserPublishedVideo } from "@/store/calling/utils";
import { vuexName, MODULES } from "@/store/utils";
import { MediaDeviceError, getMediaDeviceErrorMessage } from "@/agora/utils";
import { useDeviceToggling } from "@/hooks/useDeviceToggling";
import { DynamicModalNames } from "@/store/modal/actions";
import { useDeviceSettingModal } from "@/hooks/useDeviceSettingModal";

export default defineComponent({
  name: "TeacherClassHelperCard",
  props: {
    isMinimized: Boolean,
    onClickMinimized: {
      type: Object as () => void,
      required: true,
    },
  },
  components: {
    LoadingOutlined,
    NameTag,
    CloseOutlined,
    DownOutlined,
    UpOutlined,
    Button,
    VideoCameraIcon,
    VideoCameraSlashIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
    XMarkIcon,
    Cog6ToothIcon,
    EyeIcon,
    EyeSlashIcon,
    ArrowsPointingInIcon,
    WifiIcon,
  },
  setup() {
    const { getters, dispatch, commit } = useStore();
    const { toggleModal } = useDeviceSettingModal();
    const { toggleMicrophone, toggleCamera, isCameraToggling, isMicrophoneToggling } = useDeviceToggling();
    const poorConnectionText = computed(() => fmtMsg(LowBandWidthLocales.VideoLowBandWidthMessage));
    const helperInfo = computed<HelperState | undefined>(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_HELPER_INFO)]);
    const dynamicModalName = computed<string>(() => getters[vuexName(MODULES.MODAL.GETTERS.NAME)]);
    const isLowBandWidth = computed(() => getters["getLowBandWidth"]);
    const isAudioPublished = computed<boolean>(() => isUserPublishedAudio({ userId: helperInfo.value?.id ?? "", gettersOrRootGetters: getters }));
    const isVideoPublished = computed<boolean>(() => isUserPublishedVideo({ userId: helperInfo.value?.id ?? "", gettersOrRootGetters: getters }));
    const isJoinedSession = computed<boolean>(() => getters[vuexName(MODULES.CALLING.GETTERS.GET_IS_JOINED_SESSION)]);
    const isTeacherAllowStudentSeeHelperVideo = computed<boolean>(
      () => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_TEACHER_ALLOW_STUDENT_SEE_HELPER_VIDEO)],
    );
    const helperId = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_HELPER_ID)]);
    const isMeHelper = computed<boolean>(() => getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)]);
    const oneAndOne = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)]);
    const anotherTeacherIsInOneToOneMode = computed(
      () => !!getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER_ID)],
    );
    const cameraError = computed<MediaDeviceError>(() => getters[vuexName(MODULES.CALLING.GETTERS.GET_CAMERA_ERROR)]);
    const microphoneError = computed<MediaDeviceError>(() => getters[vuexName(MODULES.CALLING.GETTERS.GET_MICROPHONE_ERROR)]);
    const cameraErrorMessage = computed(() => getMediaDeviceErrorMessage(cameraError.value));
    const microphoneErrorMessage = computed(() => getMediaDeviceErrorMessage(microphoneError.value));
    const toggleHelperVideoLoading = ref(false);
    // Teacher hide or show helper video for everyone in the class
    const toggleHelperVideo = async (isShown = false) => {
      if (isTeacherAllowStudentSeeHelperVideo.value === isShown || toggleHelperVideoLoading.value) return;
      try {
        toggleHelperVideoLoading.value = true;
        await HelperService.teacherToggleHelperVideo(isShown);
      } catch (error) {
        Logger.error(error);
      }
      toggleHelperVideoLoading.value = false;
    };

    const onHelperToggleCamera = async () => {
      await toggleCamera(async () => {
        let isOff = false;
        if (isVideoPublished.value) {
          isOff = true;
        }
        await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_HELPER_CAMERA), isOff);
      });
    };
    const onHelperToggleMicro = async () => {
      await toggleMicrophone(async () => {
        let isOff = false;
        if (isAudioPublished.value) {
          isOff = true;
        }
        await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_HELPER_MICRO), isOff);
      });
    };

    const handleClickShowDeviceModal = () => {
      toggleModal(true, {
        isMicEnabled: isAudioPublished.value,
        isCamEnabled: isVideoPublished.value,
        onUpdateMicOpen: async (isOpen: boolean) => {
          await toggleMicrophone(async () => {
            await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_HELPER_MICRO), !isOpen);
          });
        },
        onUpdateCamOpen: async (isOpen: boolean) => {
          await toggleCamera(async () => {
            await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_HELPER_CAMERA), !isOpen);
          });
        },
      });
    };

    const openDeviceWarningModal = async (isCamera: boolean) => {
      await dispatch(vuexName(MODULES.MODAL.DISPATCHES.INFORM_DEVICE_WARNING), {
        code: isCamera ? cameraError.value.code : microphoneError.value.code,
        isCamera,
        callback: isCamera ? onHelperToggleCamera : onHelperToggleMicro,
      });
    };

    watch(isVideoPublished, async (val) => {
      if (val && dynamicModalName.value === DynamicModalNames.CameraWarning) {
        commit(vuexName(MODULES.MODAL.COMMITS.CLOSE));
      }
    });

    watch(isAudioPublished, async (val) => {
      if (val && dynamicModalName.value === DynamicModalNames.MicrophoneWarning) {
        commit(vuexName(MODULES.MODAL.COMMITS.CLOSE));
      }
    });

    const onRemoveHelper = () => {
      Modal.confirm({
        icon: createVNode(ExclamationCircleOutlined),
        content: ConfirmRemoveHelperText.value,
        okText: CommonYesText.value,
        cancelText: CommonNoText.value,
        onOk: async () => {
          await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.TEACHER_REMOVE_HELPER));
        },
      });
    };

    // TODO PRE-477: In action MANAGE_REMOTE_USER_VIDEO_AND_AUDIO_SUBSCRIPTIONS, set a new state which save all ids of video which are showing on screen, because showwing video is different with published video
    const isVideoShown = computed<boolean>(() => {
      return isMeHelper.value
        ? isVideoPublished.value
        : isVideoPublished.value && isTeacherAllowStudentSeeHelperVideo.value && !oneAndOne.value && !anotherTeacherIsInOneToOneMode.value;
    });

    const helperAvatar = computed<string>(() => generateAvatar(getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_HELPER_AVATAR)]));

    const CommonYesText = computed(() => fmtMsg(CommonLocale.CommonYesButtonText));
    const CommonNoText = computed(() => fmtMsg(CommonLocale.CommonNoButtonText));
    const ConfirmRemoveHelperText = computed(() => fmtMsg(TeacherClassLocale.ConfirmRemoveHelper));
    const showHelperVideoText = computed(() => fmtMsg(HelperLocales.ShowVideoButton));
    const hideHelperVideoText = computed(() => fmtMsg(HelperLocales.HideVideoButton));
    return {
      helperInfo,
      isTeacherAllowStudentSeeHelperVideo,
      isMeHelper,
      isVideoPublished,
      isAudioPublished,
      onHelperToggleCamera,
      onHelperToggleMicro,
      handleClickShowDeviceModal,
      onRemoveHelper,
      toggleHelperVideoLoading,
      toggleHelperVideo,
      showHelperVideoText,
      hideHelperVideoText,
      isVideoShown,
      helperAvatar,
      helperId,
      VideoCameraIcon,
      VideoCameraSlashIcon,
      SpeakerWaveIcon,
      SpeakerXMarkIcon,
      EyeIcon,
      EyeSlashIcon,
      cameraError,
      microphoneError,
      cameraErrorMessage,
      microphoneErrorMessage,
      isJoinedSession,
      isCameraToggling,
      isMicrophoneToggling,
      openDeviceWarningModal,
      isLowBandWidth,
      poorConnectionText,
    };
  },
});
